import { Button, Eyebrow } from '@tt/design-system';
import { QRCodeSVG } from 'qrcode.react';
import { useMemo, useState } from 'react';
import { CRYPTO_COST_ETH, CRYPTO_COST_USDC } from '../../../lib/env';
import {
  buildEthUri,
  buildUsdcUri,
  CRYPTO_COIN_LABEL,
  type CryptoCoin,
  formatCryptoValue,
  generateMockWallet,
  shortAddress,
} from '../../../utils/payments/mockCrypto';
import { copyToClipboard } from '../../../utils/share';
import { wait } from '../../../utils/wait';
import type { PaymentMethodProps } from '../types';
import { CoinToggle } from './CoinToggle';

type CryptoStage = 'idle' | 'connecting' | 'connected' | 'confirming';

export function CryptoMethod({
  registrationId,
  onConfirm,
  isConfirming = false,
}: PaymentMethodProps) {
  const [coin, setCoin] = useState<CryptoCoin>('eth');
  const [stage, setStage] = useState<CryptoStage>('idle');
  const [copied, setCopied] = useState(false);

  const address = useMemo(() => generateMockWallet(registrationId), [registrationId]);
  const amount = coin === 'eth' ? CRYPTO_COST_ETH : CRYPTO_COST_USDC;
  const uri =
    coin === 'eth' ? buildEthUri(address, amount) : buildUsdcUri(address, amount);

  async function handleCopy() {
    const ok = await copyToClipboard(address);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    }
  }

  async function handleConnect() {
    if (stage !== 'idle') return;
    setStage('connecting');
    await wait(1000);
    setStage('connected');
  }

  async function handleConfirmTx() {
    if (stage !== 'connected' || isConfirming) return;
    setStage('confirming');
    await wait(1500);
    setStage('idle');
    onConfirm({
      method: 'crypto',
      detailsFake: formatCryptoValue(coin, amount),
    });
  }

  const isBusy = stage !== 'idle';

  return (
    <div className="flex flex-col gap-6" aria-busy={isBusy}>
      <div className="flex items-center justify-between gap-4">
        <Eyebrow tone="gold">Cripto Web3</Eyebrow>
        <CoinToggle value={coin} onChange={setCoin} />
      </div>

      <div className="flex flex-col items-center gap-4 rounded-md border border-gold/20 bg-night/40 p-6">
        <div className="rounded-md border border-gold/30 bg-parchment p-3">
          <QRCodeSVG
            value={uri}
            size={160}
            level="M"
            fgColor="#0B0E1A"
            bgColor="#F5EFE0"
            marginSize={0}
          />
        </div>

        <p className="font-display text-3xl text-gold-shimmer">
          {formatCryptoValue(coin, amount)}
        </p>

        <div className="flex w-full max-w-xs flex-col gap-2">
          <p className="font-mono text-[10px] uppercase tracking-stamp text-champagne">
            Endereço da carteira
          </p>
          <div className="flex gap-2">
            <code
              className="min-w-0 flex-1 truncate rounded-md border border-gold/20 bg-night/60 px-3 py-2 font-mono text-xs text-parchment"
              title={address}
            >
              {shortAddress(address, 10, 6)}
            </code>
            <button
              type="button"
              onClick={handleCopy}
              className="flex-shrink-0 rounded-md border border-gold/40 px-3 py-2 font-mono text-[10px] uppercase tracking-stamp text-champagne transition-colors duration-fast hover:border-gold hover:text-gold"
              aria-label="Copiar endereço da carteira"
            >
              {copied ? 'Copiado ✓' : 'Copiar'}
            </button>
          </div>
        </div>

        <p className="text-center font-mono text-[10px] uppercase tracking-stamp text-champagne/70">
          Aponte sua carteira para o QR Code · rede {CRYPTO_COIN_LABEL[coin]}
        </p>
      </div>

      <div className="border-t border-gold/15 pt-6">
        {stage === 'idle' || stage === 'connecting' ? (
          <Button
            type="button"
            variant="secondary"
            size="lg"
            fullWidth
            disabled={stage === 'connecting' || isConfirming}
            onClick={handleConnect}
          >
            {stage === 'connecting' ? 'Conectando carteira…' : 'Conectar carteira'}
          </Button>
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-center font-mono text-[10px] uppercase tracking-stamp text-gold">
              Carteira conectada · {shortAddress(address, 6, 4)}
            </p>
            <Button
              type="button"
              variant="primary"
              size="lg"
              fullWidth
              onClick={handleConfirmTx}
              disabled={isConfirming || stage === 'confirming'}
            >
              {isConfirming
                ? 'Eternizando seu registro…'
                : stage === 'confirming'
                  ? 'Aguardando confirmação da rede…'
                  : 'Confirmar transação'}
            </Button>
          </div>
        )}
        <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-stamp text-champagne/60">
          Modo demonstração · nenhuma carteira real será conectada
        </p>
      </div>
    </div>
  );
}
